import axios from 'axios'
import { user } from 'ampersand-app'
import config from '@/config'
import getCollaborationRequestHeader from '@/modules/document/_helpers/getCollaborationRequestHeader'
import { getDmsTokenContextKey, setDmsToken } from '@/redux/ducks/tenant/settings'
import { dispatch, getAppState } from '@/redux/store'
import { DmsRequestContextChangedError } from './dms-request-context-error'

export interface DmsFrontendAuth {
  dmsApiClient: string,
  dmsToken: string,
}

interface RequestFailure {
  response?: { status?: number },
}

interface DmsRequestContext {
  collaborationId: string,
  key: string,
  sessionEpoch: number,
}

const refreshes = new Map<string, Promise<string>>()

const currentCollaborationId = (): string => {
  const current = getAppState().document.list.currentCollaboration
  return current && current !== 'internal' ? current : 'internal'
}

const currentDmsRequestContext = (): DmsRequestContext => {
  const collaborationId = currentCollaborationId()
  const sessionEpoch = getAppState().tenant.settings.dmsTokenSessionEpoch ?? 0
  return {
    collaborationId,
    key: getDmsTokenContextKey(sessionEpoch, collaborationId),
    sessionEpoch,
  }
}

const assertContextIsCurrent = (context: DmsRequestContext): void => {
  if (currentDmsRequestContext().key !== context.key) {
    throw new DmsRequestContextChangedError()
  }
}

const trustedTokenFor = (context: DmsRequestContext): string | undefined => {
  const settings = getAppState().tenant.settings
  return settings.dmsTokenContextKey === context.key ? settings.dmsToken : undefined
}

const fetchDmsFrontendToken = (context: DmsRequestContext, forceRefresh: boolean): Promise<string> => {
  const existing = refreshes.get(context.key)
  if (existing) return existing

  const refresh = axios.get(`${config.resourceHost}tenant/collaborators/${context.collaborationId}/dmsToken`, {
    headers: {
      Authorization: `Bearer ${user.token}`,
      ...(context.collaborationId === 'internal' ? {} : getCollaborationRequestHeader(context.collaborationId)),
    },
    ...(forceRefresh ? { params: { refresh: true } } : {}),
  }).then(({ data: { data: dmsToken } }) => {
    if (!dmsToken) throw new Error('DMS frontend token is missing')

    if (currentDmsRequestContext().key === context.key) {
      dispatch(setDmsToken(dmsToken, context.collaborationId, context.sessionEpoch))
    }
    return dmsToken
  }).finally(() => {
    refreshes.delete(context.key)
  })

  refreshes.set(context.key, refresh)
  return refresh
}

const isAuthenticationFailure = (error: RequestFailure): boolean =>
  error?.response?.status === 401 || error?.response?.status === 403

export const withDmsFrontendToken = async<T>(
  request: (auth: DmsFrontendAuth) => Promise<T>,
): Promise<T> => {
  const context = currentDmsRequestContext()
  const settings = getAppState().tenant.settings
  // A request that starts while another one refreshes must not knowingly send the stale token.
  const refreshInProgress = refreshes.get(context.key)
  const dmsToken = refreshInProgress
    ? await refreshInProgress
    : trustedTokenFor(context) || await fetchDmsFrontendToken(context, false)
  assertContextIsCurrent(context)

  try {
    const result = await request({ dmsApiClient: settings.dmsApiClient, dmsToken })
    assertContextIsCurrent(context)
    return result
  } catch (error) {
    assertContextIsCurrent(context)
    if (!isAuthenticationFailure(error as RequestFailure)) throw error

    // Another request may already have refreshed this context after our first attempt started.
    // Reuse that newer token instead of invalidating it and forcing a second DMS login.
    const currentToken = trustedTokenFor(context)
    const retryToken = currentToken && currentToken !== dmsToken
      ? currentToken
      : await fetchDmsFrontendToken(context, true)
    assertContextIsCurrent(context)

    const result = await request({
      dmsApiClient: getAppState().tenant.settings.dmsApiClient,
      dmsToken: retryToken,
    })
    assertContextIsCurrent(context)
    return result
  }
}
